import Vue from 'vue'
import Component from 'vue-class-component'
import {Inject, Prop, Watch} from 'vue-property-decorator'
import Api from '@/services/Api'
import _ from 'lodash'
import {TableHeader} from '@/components/common/TableHeader'
import {BrowserFilters} from '@/types/BrowserFilters'
import {BackendException, ValidationErrors} from '@/utils/HttpClient'
import {capitalizeFirstLetter} from '@/utils/string'

export type Header<ModelType> = TableHeader<ModelType>

@Component
export default class Browser<ModelType> extends Vue
{
    @Inject('api')
    protected api!: Api

    @Prop(Object)
    filters!: BrowserFilters

    @Watch('filters')
    async onFiltersChange(filters: BrowserFilters, old: BrowserFilters) {
        if (!_.isEqual(filters, old)) {
            await this.refresh()
        }
    }

    @Prop({type: Array, default: () => []})
    headers!: Header<ModelType>[]
    defaultHeaders: Header<ModelType>[] = []

    list: ModelType[] = []

    get realHeaders(): Header<ModelType>[] {
        return this.headers.length > 0 ? this.headers : this.defaultHeaders
    }

    async created() {
        await this.fetchList()
    }

    async fetchList() {
        return
    }

    async refresh() {
        await this.fetchList()
        return true
    }

    // noinspection JSUnusedGlobalSymbols
    editItem(item: ModelType) {
        this.$emit('edit', item)
    }

    // noinspection JSUnusedGlobalSymbols
    async deleteItem(item: ModelType) {
        if (await this.confirmDeletion(item)) {
            try {
                let isDeleted = true
                if ((item as unknown as { id: number }).id) {
                    isDeleted = await this.deleteModel(item)
                }
                if (isDeleted) {
                    this.list = this.list.filter(r => r !== item)
                    this.showMessage(this.getDeletionSuccessMessage(item))
                }
            } catch (ex) {
                let errors = [] as string[]
                if (ex instanceof ValidationErrors) {
                    errors = errors.concat(...Object.values(ex))
                } else if (ex instanceof BackendException) {
                    errors = errors.concat([ex.message])
                }
                if (errors.length > 0) {
                    this.showMessage(`${this.getDeletionFailMessage(item)}: ${errors.join('; ')}`)
                }
            }
        }
    }

    getModelIdentifiableDisplayName(model: ModelType, isSecondCase = false): string {
        const id = this.getModelDisplayId(model)
        return this.getModelDisplayName(isSecondCase) + (id ? ' ' + id : '')
    }

    getModelDisplayId(model: ModelType): string {
        if (model) {
            const m = model as unknown as { id?: string }
            if (m.id != null) {
                return '#' + m.id
            }
        }
        return ''
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getModelDisplayName(isSecondCase: boolean): string {
        return 'объект'
    }

    getModelDeletedWord(): string {
        return 'удалён'
    }

    getDeletionConfirmPrompt(model: ModelType): string {
        return `Вы уверены, что хотите удалить ${this.getModelIdentifiableDisplayName(model, true)}?`
    }

    getDeletionSuccessMessage(model: ModelType): string {
        return `${capitalizeFirstLetter(this.getModelIdentifiableDisplayName(model))} ${this.getModelDeletedWord()}`
    }

    getDeletionFailMessage(model: ModelType): string {
        return `Не удалось удалить ${this.getModelIdentifiableDisplayName(model, true)}`
    }

    async confirm(prompt: string): Promise<boolean> {
        return confirm(prompt)
    }

    async confirmDeletion(model: ModelType): Promise<boolean> {
        return await this.confirm(this.getDeletionConfirmPrompt(model))
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async deleteModel(model: ModelType): Promise<boolean> {
        return true
    }

    getCellValue(item: ModelType, header: Header<ModelType>): number | string | null {
        if (header.value instanceof Function) {
            return header.value(item)
        }
        if (typeof header.value === 'string') {
            return item[header.value] as unknown as number | string | null
        }
        return null
    }

    showMessage(text: string) {
        this.$store.commit('addMessage', text)
    }
}
